import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useOpenAsyncUrl } from "@src/mobile/utils/openUrl";
import { useCallback } from "react";

import { buildZendeskSsoUrl } from "./buildZendeskSsoUrl";
import { FACILITY_HELP_SUPPORT_LINK } from "./paths";
import { useWorkplaceZendeskSsoToken } from "./useWorkplaceZendeskSsoToken";

/** Public Help Center URL used when SSO minting fails. */
export function buildPublicFallbackUrl(): string {
  return FACILITY_HELP_SUPPORT_LINK;
}

/**
 * Returns a click handler that mints a fresh Zendesk SSO token and opens the
 * authenticated Help Center in an in-app browser (native) or new tab (web).
 *
 * Uses `useOpenAsyncUrl` so the token can be minted after the click without
 * tripping Safari's popup blocker. The returned function MUST be called
 * synchronously inside the click handler. On mint failure it degrades to the
 * public Help Center rather than leaving the user on a blank tab.
 */
export function useOpenZendeskHelpCenter() {
  const openAsyncUrl = useOpenAsyncUrl();
  const { mutateAsync: mintSsoToken } = useWorkplaceZendeskSsoToken();

  return useCallback(() => {
    void openAsyncUrl(async () => {
      try {
        const { jwt } = await mintSsoToken();
        logEvent(APP_V2_USER_EVENTS.HELP_CENTER_SSO_SUCCESS);
        return buildZendeskSsoUrl(jwt);
      } catch (error) {
        logEvent(APP_V2_USER_EVENTS.HELP_CENTER_SSO_FAILED, { error });
        return buildPublicFallbackUrl();
      }
    });
  }, [openAsyncUrl, mintSsoToken]);
}
